"use client";

import { useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

import MarketTooltip from "./components/MarketTooltip/MarketTooltip";
import { useChangeTooltipStatus } from "./hooks/useChangeTooltipStatus";
import IconTrendingDown from "./icons/IconTrendingDown";
import IconTrendingUp from "./icons/IconTrendingUp";
import * as S from "./StatusChip.style";
import { IconMinus } from "../../../../components";
import {
  Alignment,
  Position,
} from "../../../../shared/components/core/Tooltip/types/tooltip.types";
import { CompanyInfo } from "../../types/marketInfo.types";

type Props = {
  marketInfo: CompanyInfo;
  chipStatus: "up" | "keep" | "down";
  size?: "md" | "lg";
  isInteractive?: boolean;
  isMobileTextColumn?: boolean;
  isStockIcon?: boolean;
  /**
   * true 면 툴팁을 body 로 Portal 하여 스크롤 컨테이너(overflow) 에 잘리지 않게 한다.
   * 상세 Preview 패널처럼 조상에 overflow 가 걸린 경우 사용.
   */
  isPortalTooltip?: boolean;
};

// 툴팁(MarketTooltip) 대략 높이. Portal 위 아래 뒤집기 판단용.
const TOOLTIP_ESTIMATED_HEIGHT = 160;

const StatusChip = ({
  marketInfo,
  chipStatus,
  size = "md",
  isInteractive = true,
  isMobileTextColumn = false,
  isStockIcon = true,
  isPortalTooltip = false,
}: Props) => {
  const chipRef = useRef<HTMLDivElement>(null);

  const {
    tooltipRef,
    changeTooltipPosition,
    resetTooltipPosition,
    touchToChangeTooltipStatus,
    showTooltip,
    tooltipPosition,
  } = useChangeTooltipStatus();

  // Portal 모드 전용 상태(공유 훅 대신 chip 화면 좌표로 직접 계산).
  const [portalRect, setPortalRect] = useState<DOMRect | null>(null);
  const [portalPosition, setPortalPosition] = useState<{
    position: Position;
    alignment: Alignment;
  }>({ position: "top", alignment: "center" });

  const openPortalTooltip = () => {
    const anchor = chipRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const alignment: Alignment = (() => {
      if (rect.right + 30 > window.innerWidth) return "right";
      if (rect.left < 0) return "left";
      return "center";
    })();
    // position "top" = 칩 아래로, "bottom" = 칩 위로 표출(MarketTooltip 오프셋 규칙).
    const hasRoomBelow =
      rect.bottom + TOOLTIP_ESTIMATED_HEIGHT <= window.innerHeight;
    const position: Position =
      !hasRoomBelow && rect.top > TOOLTIP_ESTIMATED_HEIGHT ? "bottom" : "top";

    setPortalPosition({ position, alignment });
    setPortalRect(rect);
  };

  const closePortalTooltip = () => setPortalRect(null);

  useEffect(() => {
    const checkIsOutsideTouch = (event: TouchEvent) => {
      const currentTarget = event.target as HTMLElement;
      const isOutside = !chipRef.current?.contains(currentTarget);

      if (isOutside && showTooltip) {
        event.preventDefault();

        return resetTooltipPosition();
      }
    };
    window.addEventListener("touchend", checkIsOutsideTouch);

    return () => {
      window.removeEventListener("touchend", checkIsOutsideTouch);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTooltip]);

  const { exchange, symbol, roc, pre } = marketInfo;

  const returnCurrentIcon = () => {
    const iconSize = size === "md" ? 16 : 24;

    switch (chipStatus) {
      case "up":
        return <IconTrendingUp width={iconSize} height={iconSize} />;
      case "down":
        return <IconTrendingDown width={iconSize} height={iconSize} />;
      default:
        return <IconMinus width={iconSize} height={iconSize} color="#515C6C" />;
    }
  };

  const statusSymbol = (() => {
    if (chipStatus === "up") return "+";

    return "";
  })();

  const preMarketText = pre ? "(pre-market)" : "";
  const chipText = `${exchange}: ${symbol} | ${statusSymbol} ${
    roc === 0 ? "0.00" : roc?.toFixed(2)
  }%`;

  const handleMouseEnter = isPortalTooltip
    ? openPortalTooltip
    : changeTooltipPosition;
  const handleMouseLeave = isPortalTooltip
    ? closePortalTooltip
    : resetTooltipPosition;
  const handleTouchEnd = isPortalTooltip
    ? () => (portalRect ? closePortalTooltip() : openPortalTooltip())
    : touchToChangeTooltipStatus;

  return (
    <S.ChipWrapper>
      <S.StatusChip
        ref={chipRef}
        $size={size}
        $chipStatus={chipStatus}
        $isInteractive={isInteractive}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchEnd={handleTouchEnd}
        className="stock-chip"
      >
        {isStockIcon && (
          <S.StockIconWrapper className="stock-icon">
            {returnCurrentIcon()}
          </S.StockIconWrapper>
        )}
        <S.ChipText
          $size={size}
          $chipStatus={chipStatus}
          $isMobileTextColumn={isMobileTextColumn}
          className="stock-chip-text-wrapper"
        >
          <S.StockText className="stock-chip-text">{chipText}</S.StockText>
          {preMarketText && (
            <S.PreMarketTextWrapper>
              &nbsp;{preMarketText}
            </S.PreMarketTextWrapper>
          )}
        </S.ChipText>
      </S.StatusChip>
      {isInteractive && !isPortalTooltip && (
        <MarketTooltip
          marketInfo={marketInfo}
          chipStatus={chipStatus}
          showTooltip={showTooltip}
          tooltipPosition={tooltipPosition}
          ref={tooltipRef}
        />
      )}
      {isInteractive &&
        isPortalTooltip &&
        portalRect &&
        typeof document !== "undefined" &&
        createPortal(
          <S.PortalTooltipAnchor
            style={{
              top: portalRect.top,
              left: portalRect.left,
              width: portalRect.width,
              height: portalRect.height,
            }}
          >
            <MarketTooltip
              marketInfo={marketInfo}
              chipStatus={chipStatus}
              showTooltip
              tooltipPosition={portalPosition}
              ref={tooltipRef}
            />
          </S.PortalTooltipAnchor>,
          document.body,
        )}
    </S.ChipWrapper>
  );
};

export default StatusChip;
